/* eslint-disable max-len */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { ContentItemUnion, ContentStatusType, ContentType, ContentTypeKey } from '../../../../../../../ResourcesRevised/types';
import { IoIosArrowForward, IoIosArrowBack } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { useScroll } from '../../../../../../../../libs/CustomHooks/useScroll';
import useResourceHelper from './useResourceHelper';
import { useSearchParams, useParams } from 'react-router-dom';
// Import the needed functions and types from content_graphql_api
import contentGraphQLApi from '../../../../../../../actions/resource_graphql_actions/content_graphql_api';
import { ContentStatus, ContentType as GraphQLContentType, TimePeriod, Image as GraphQLImage, Category as GraphQLCategory } from '../../../../../../../actions/resource_graphql_actions/content_graphql_types';
import useDidMountEffect from '../../../../../../../../libs/CustomHooks/UseDidMountEffect';
import { CategoryData } from '../../StatusContentSection/CategoriesView/types';
import CategoriesCard from '../../../../../../../ResourcesRevised/Components/Resuable/CategoriesCards';
import MainCard from '../../../../../../../../UiComponents/Cards/MainCard';
import NoData from '../../../../../../../../components/Common/NoData';
import ResourceShimmerCard from './ResourceShimmerCard';

// Interface for pagination state
interface PaginationState {
	total: number;
	page: number;
	limit: number;
	pages?: number;
}

// Extract the getContent and searchContent functions from the imported API
const { getContent, searchContent } = contentGraphQLApi;

interface CategoryCardsListProps {
	itemsPerView?: number;
	viewAll: boolean;
	handleViewAll?: (isViewAll: ContentTypeKey) => void;
	searchValue?: string;
	subcategoryOnly?: boolean;
	onCountUpdate?: (count: number) => void;
}

const CategoryCardsList: React.FC<CategoryCardsListProps> = ({
	itemsPerView = 4,
	viewAll,
	handleViewAll,
	searchValue,
	subcategoryOnly = false,
	onCountUpdate
}) =>
{
	// Local index state for pagination
	const [ currentIndex, setCurrentIndex ] = useState(0);
	// State for storing categories from API
	const [ categories, setCategories ] = useState<any[]>([]);
	// Loading state
	const [ isLoading, setIsLoading ] = useState(true);
	// Screen width state for responsive design
	const [ screenWidth, setScreenWidth ] = useState(window.innerWidth);
	// Card dimensions
	const cardWidth = 270; // Width of each card in pixels
	const gap = 16; // Gap between cards in pixels

	// Calculate number of cards that can fit in the viewport
	const cardsPerPage = useMemo(() =>
	{
		const containerWidth = screenWidth - 32; // Accounting for padding
		const cardsPerView = Math.floor((containerWidth + gap) / (cardWidth + gap));

		return Math.max(1, cardsPerView); // Ensure at least 1 card is shown
	}, [ screenWidth ]);

	// Update screen width on resize
	useEffect(() =>
	{
		const handleResize = () =>
		{
			setScreenWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Pagination state for API response
	const [ pagination, setPagination ] = useState<PaginationState>({
		total : 0,
		page  : 0,
		limit : 0,
		pages : 0
	});
	const totalIndex = useMemo(() => Math.floor(pagination.total / cardsPerPage), [ pagination.total, cardsPerPage ]);
	const totalCardsTillCurrentIndex = useMemo(() => cardsPerPage + cardsPerPage * currentIndex, [ cardsPerPage, currentIndex ]);

	// Current page for pagination
	const [ page, setPage ] = useState(1);
	const itemsPerPage = 10;
	// Refs for infinite scrolling
	const lastEleRef = useRef(null);
	const loaderRef = useRef<boolean>(false);
	const [ searchParams ] = useSearchParams();
	const { categoryId, companyId: networkCompanyId } = useParams();

	const sessionCompanyId = useSelector((state: any) => state?.mysession?.session?.companyId);
	const companyId = networkCompanyId || sessionCompanyId;
	const { isAdminView, isFavoritesList, getDateFilterValue } = useResourceHelper();

	// Fetch images with pagination
	const fetchCategories = async () =>
	{
		try
		{
			setIsLoading(true);
			loaderRef.current = true;
			const dateFilter = searchParams.get('dateFilter') as TimePeriod;
			const memberFilter = searchParams.get('memberFilter');

			// Get the proper TimePeriod enum value
			// const timeRangePeriod = getFilterTimePeriod();

			// Get sort parameters from URL

			// Create options object for graphqlGetCategories with the correct types
			const graphqlOptions:any = {
				contentType : subcategoryOnly
					? GraphQLContentType.SUBCATEGORY
					: GraphQLContentType.CATEGORY,
				page,
				limit   : itemsPerPage,
				from    : isAdminView ? 'Configuration' as const : 'MyResource' as const,
				// filters not required in backend for searchSubcategories and categories
				filters : {
					// ...(categoryId && { categoryIds: [ categoryId ] }),
					...(isFavoritesList && { favorited: true }),
					...(!isAdminView && { isEnabled: true }),
					// status      : contentStatus as unknown as ContentStatus
					timeRange : (dateFilter && dateFilter !== 'ALL_TIME') ? getDateFilterValue(dateFilter) : undefined,
					...(memberFilter && memberFilter !== 'all' && { createdBy: memberFilter })
				},
				...(categoryId && { parentCategoryId: categoryId })

			};

			if (!isAdminView)
			{
				graphqlOptions.filters.isEnabled = true;
			}

			// Call getContent with the companyId and options
			// const response = await getContent(companyId, contentOptions);
			const response = await searchContent(companyId, searchValue || '', graphqlOptions);

			if (response.data)
			{
				// Map the response data to CategoryData format
				const categoryData = response.data as unknown as CategoryData[];

				// If it's the first page, replace the data, otherwise append
				const newCategories = page === 1
					? categoryData
					: [ ...categories, ...categoryData ];

				setCategories(newCategories);

				if (onCountUpdate)
				{
					onCountUpdate(newCategories.length);
				}
				// if (newCategories.length > 0)
				// {
				// 	setNoData(false);
				// }

				// Set pagination data if available
				if (response.pagination)
				{
					setPagination(response.pagination);
				}
			}
		}
		catch (error)
		{
			console.error('Error fetching categories:', error);
		}
		finally
		{
			setIsLoading(false);
			loaderRef.current = false;
		}
	};

	// Use the custom scroll hook for infinite scrolling
	useScroll({
		lastEleRef,
		loaderRef,
		count : pagination.total,
		data  : categories,
		setPage
	});

	// Initial fetch and when dependencies change
	useEffect(() =>
	{
		if (companyId)
		{
			fetchCategories();
		}
	}, [ companyId, page, searchValue ]);

	// Handle filter changes via search params
	useDidMountEffect(() =>
	{
		if (companyId)
		{
			page > 1
				? setPage(1)
				: fetchCategories();
		}
	}, [ searchParams, categoryId ]);

	// Listen for subcategory created event
	useEffect(() =>
	{
		const handleSubCategoryCreated = (event: any) =>
		{
			const { parentCategoryId } = event.detail || {};

			// Refetch if no parentCategoryId specified or if it matches current categoryId
			if (!parentCategoryId || parentCategoryId === categoryId)
			{
				// Reset to first page and refetch
				setPage(1);
				setCategories([]);
				fetchCategories();
			}
		};

		// Register listener
		window.addEventListener('subcategoryCreated', handleSubCategoryCreated);

		// Cleanup
		return () =>
		{
			window.removeEventListener('subcategoryCreated', handleSubCategoryCreated);
		};
	}, [ categoryId, companyId ]);

	// Handler for toggling viewAll state
	const toggleViewAllLocal = () =>
	{
		if (handleViewAll)
		{
			handleViewAll((subcategoryOnly ? 'subcategories' : 'categories') as ContentTypeKey);
		}
	};

	// Local arrow navigation handler
	const handleArrowClick = (direction: 'next' | 'prev') =>
	{
		if (direction === 'next')
		{
			if (cardsPerPage + cardsPerPage * (currentIndex + 1) >= categories.length)
			{
				setPage(page + 1);
			}
			setCurrentIndex((prev) => Math.min(prev + 1, totalIndex));
		}
		else
		{
			setCurrentIndex((prev) => Math.max(prev - 1, 0));
		}
	};

	// No images to display
	if (categories.length === 0 || (page === 1 && isLoading))
	{
		// If loading on first page, show shimmer
		if (isLoading && page === 1)
		{
			return (
				<Grid item py={1} width={'100%'}>
					<Stack
						pb={1.5}
						direction={'row'}
						alignItems={'center'}
						justifyContent={'space-between'}
						width={'100%'}
					>
						<Stack direction={'row'} alignItems={'center'} spacing={0.5}>
							<Typography fontWeight={500}>
								{subcategoryOnly ? 'Subcategories' : 'Categories'}
							</Typography>
						</Stack>
					</Stack>
					<ResourceShimmerCard totalCards={viewAll ? 5 : 3} />
				</Grid>
			);
		}

		// If not loading and no categories, return null (parent will handle "No Data" message)
		if (!isLoading && categories.length === 0)
		{
			return null;
		}
	}

	// const location = window.location.pathname;

	// Calculate items to display based on pagination or view all settings
	const displayCategories = !viewAll
		? categories.slice(
			currentIndex * cardsPerPage,
			totalCardsTillCurrentIndex
		)
		: [];

	return (
		<Grid container width={'100%'} height={'calc(100% - 58px)'}>
			<Stack
				pb={1.5}
				direction={'row'}
				alignItems={'center'}
				justifyContent={'space-between'}
				width={'100%'}
			>
				<Stack direction={'row'} alignItems={'center'} spacing={0.5}>
					<Typography fontWeight={500}>
						{subcategoryOnly ? 'Sub Categories' : 'Categories'}
					</Typography>
					<Typography fontWeight={500}>
						({pagination.total})
					</Typography>
				</Stack>

				{/* {!viewAll && */}
				<Button
					variant='text'
					size='small'
					sx={{ textTransform: 'none !important' }}
					onClick={toggleViewAllLocal}
				>
					{viewAll ? 'View Less' : 'View All'}
				</Button>
				{/* } */}
			</Stack>

			{viewAll
				? (
					<Grid item container p={2}>
						<MainCard
							sx={{ width: '100%', padding: '0 !important' }}
							// contentSX={{ paddingBottom: '16px !important' }}
							border={false}
							boxShadow={false}
						>
							<Grid container spacing={2}>
								{categories.map((category, index) => category?._id && (
									<Grid item xs={12} sm={6} md={3} key={category._id || index}>
										<CategoriesCard
											shouldShowPopover
											ContentCategoryType
											status={category.isEnabled ? 'Enabled' : 'Disabled'}
											category={category} // Pass the full category object
											onDeleteSuccess={() =>
											{
												setPage(1); // Reset to first page
												setCategories([]); // Clear categories
												fetchCategories(); // Refetch categories
											}}
										/>
									</Grid>
								))}
							</Grid>
						</MainCard>
						{/* Element for intersection observer */}
						{!isLoading && categories.length > 0 &&
						categories.length < pagination.total && (
							<div ref={lastEleRef} style={{ height: '2px', width: '100%' }} />
						)}
					</Grid>
				)
				: (
					<Box
						id='auto-scrollId'
						sx={{
							position             : 'relative',
							width                : '100%',
							'&:hover .nav-arrow' : {
								opacity    : 1,
								visibility : 'visible'
							}
						}}
						display='flex'
					>
						<Box
							pb={1.5}
							sx={{
								display                : 'flex',
								position               : 'relative',
								overflowY              : 'visible',
								gap                    : '16px',
								width                  : '100%',
								scrollbarGutter        : 'stable',
								'&::-webkit-scrollbar' : { display: 'none' },
								'-ms-overflow-style'   : 'none',
								'scrollbar-width'      : 'none',
								touchAction            : 'pan-x'
							}}
							flexDirection={'row'}
						>
							{currentIndex !== 0 && (
								<Box
									className='nav-arrow'
									position='absolute'
									left='0px'
									top='2px'
									borderRadius='8px'
									height='95%'
									width={44}
									alignItems='center'
									justifyContent='center'
									display='flex'
									zIndex={9}
									sx={{
										cursor     : 'pointer',
										background : 'linear-gradient(to right, rgba(12, 12, 12, 0.78), rgba(46, 46, 46, 0.5), rgba(155, 155, 155, 0))',
										opacity    : 0,
										visibility : 'hidden',
										transition : 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
										color      : '#fff'
									}}
									onClick={() => handleArrowClick('prev')}
								>
									<IoIosArrowBack fontSize={20} />
								</Box>
							)}

							<Grid item container p={0}>
								<MainCard
									sx={{ width: '100%', padding: '0px !important' }}
									border={false}
									// boxShadow={false}

									// sx={{ width: '100%', overflow: 'visible !important' }}
									// contentSX={{ paddingBottom: '16px !important' }}
								>
									<Grid container spacing={2}>
										{displayCategories.map((category, index) => category?._id && (
											<Grid
												item
												xs={12}
												key={category._id || index}
												position={'relative'}
												sx={{ cursor: 'pointer' }}
												sm={6}
												md={3}
											>
												<CategoriesCard
													shouldShowPopover
													ContentCategoryType
													status={category.isEnabled ? 'Enabled' : 'Disabled'}
													category={category} // Pass the full category object
													onDeleteSuccess={() =>
													{
														setPage(1); // Reset to first page
														setCategories([]); // Clear categories
														fetchCategories(); // Refetch categories
													}}
												/>
											</Grid>
										))}
									</Grid>
								</MainCard>
							</Grid>
							{categories.length <= pagination.total && (totalCardsTillCurrentIndex < categories.length) && (
								<Box
									className='nav-arrow'
									position='absolute'
									right='0px'
									top='2px'
									onClick={() => handleArrowClick('next')}
									borderRadius='8px'
									height='95%'
									width={44}
									alignItems='center'
									justifyContent='center'
									display='flex'
									zIndex={9}
									sx={{
										cursor     : 'pointer',
										background : 'linear-gradient(to left, rgba(12, 12, 12, 0.78), rgba(46, 46, 46, 0.5), rgba(155, 155, 155, 0))',
										opacity    : 0,
										visibility : 'hidden',
										transition : 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
										color      : '#fff'
									}}
								>
									<IoIosArrowForward fontSize={20} />
								</Box>
							)}
						</Box>
					</Box>
				)}

			{/* Loading indicator */}
			{isLoading && page > 1 && (
				<Box sx={{ width: '100%', textAlign: 'center', p: 2 }}>
					<Typography>Loading more images...</Typography>
				</Box>
			)}
		</Grid>
	);
};

// Add display name
CategoryCardsList.displayName = 'CategoryCardsList';

export default CategoryCardsList;